import {noteClassification} from './noteClassification';
export const IMPROVEMENT_NOTE_MAPS = [
  "DM",
  "TDM",
  "Abyss",
  "Ascent",
  "Bind",
  "Breeze",
  "Corrode",
  "Fracture",
  "Haven",
  "Icebox",
  "Lotus",
  "Pearl",
  "Split",
  "Summit",
  "Sunset",
];

export const DEFAULT_ACTIVE_MAPS = [
  "Ascent",
  "Bind",
  "Breeze",
  "Corrode",
  "Haven",
  "Lotus",
  "Sunset",
];

export const SESSION_PLAN_STATUSES = ["draft", "ready", "locked", "complete"];
export const FOCUS_POINT_OUTCOMES = [
  "not_attempted",
  "improving",
  "achieved",
  "needs_rework",
];
export const IMPROVEMENT_NOTE_STATUSES = [
  "active",
  "planned",
  "improving",
  "blocked",
  "resolved",
];

export const UNPLANNED_MATCH_TAG = "Unplanned match";

function cleanText(value, maxLength = 240) {
  return String(value || "").trim().slice(0, maxLength);
}

export function isScrimEvent(event) {
  return String(event?.tag || "").trim().toLowerCase() === "scrim";
}

export function normalizeMapName(value) {
  return cleanText(value, 64);
}

export function getImprovementNoteMapOptions(mapDocuments = []) {
  const documentsByName = new Map();
  mapDocuments.forEach((map) => {
    const name = normalizeMapName(map?.name || map?.mapName || map?.id);
    if (!name) return;
    const key = name.toLowerCase();
    if (!documentsByName.has(key)) {
      documentsByName.set(key, { ...map, id: map?.id || name, name });
    }
  });

  const includedNames = new Set(IMPROVEMENT_NOTE_MAPS.map((name) => name.toLowerCase()));
  return [
    ...IMPROVEMENT_NOTE_MAPS.map((name) => documentsByName.get(name.toLowerCase()) || { id: name, name }),
    ...Array.from(documentsByName.values()).filter((map) => !includedNames.has(map.name.toLowerCase())),
  ];
}

export function normalizeFocusPoint(point, index = 0) {
  const text = cleanText(point?.text, 12000);
  return {
    id: cleanText(point?.id, 120) || `focus-${index}-${Date.now()}`,
    text,
    ...noteClassification(point),
    sourceType: cleanText(point?.sourceType, 40) || "custom",
    sourceId: cleanText(point?.sourceId, 160) || null,
    sourceScope: cleanText(point?.sourceScope, 40) || null,
    sourceMapId: cleanText(point?.sourceMapId, 160) || null,
    outcome: FOCUS_POINT_OUTCOMES.includes(point?.outcome)
      ? point.outcome
      : "not_attempted",
    outcomeNote: cleanText(point?.outcomeNote, 500),
    evidence: Array.isArray(point?.evidence)
      ? point.evidence
          .map((item) => cleanText(item, 300))
          .filter(Boolean)
          .slice(0, 12)
      : [],
    createdAt: point?.createdAt || new Date().toISOString(),
  };
}

export function normalizeSessionPlan(plan = {}, event = null) {
  const focusPoints = Array.isArray(plan.focusPoints)
    ? plan.focusPoints.map(normalizeFocusPoint).filter((point) => point.text).slice(0, 3)
    : [];

  return {
    eventId: cleanText(plan.eventId || event?.eventId, 160),
    objective: cleanText(plan.objective, 280),
    focusPoints,
    mapId: cleanText(plan.mapId || event?.mapId, 160) || null,
    mapName: normalizeMapName(plan.mapName || event?.mapName) || null,
    status: SESSION_PLAN_STATUSES.includes(plan.status) ? plan.status : "draft",
    notes: cleanText(plan.notes, 1200),
    lockedByUid: cleanText(plan.lockedByUid, 160) || null,
    lockedBy: cleanText(plan.lockedBy, 120) || null,
    lockedAt: plan.lockedAt || null,
    createdByUid: cleanText(plan.createdByUid, 160) || null,
    createdBy: cleanText(plan.createdBy, 120) || null,
    updatedByUid: cleanText(plan.updatedByUid, 160) || null,
    updatedBy: cleanText(plan.updatedBy, 120) || null,
    createdAt: plan.createdAt || null,
    updatedAt: plan.updatedAt || null,
  };
}

export function buildFocusPointSnapshot({
  text,
  sourceType = "custom",
  sourceId = null,
  sourceScope = null,
  sourceMapId = null,
  ...classification
}) {
  return normalizeFocusPoint({
    id: `focus-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text,
    sourceType,
    sourceId,
    sourceScope,
    sourceMapId,
    ...noteClassification(classification, {scope:sourceScope, mapId:sourceMapId}),
    outcome: "not_attempted",
    createdAt: new Date().toISOString(),
  });
}

export function formatPlanningStatus(value) {
  return String(value || "draft")
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

export function formatFocusOutcome(value) {
  return String(value || "not_attempted")
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

export function getTeamMapPool(team = {}, mapDocuments = []) {
  const configuredMaps = Array.isArray(team.activeMapPool)
    ? team.activeMapPool.map(normalizeMapName).filter(Boolean)
    : [];
  if (configuredMaps.length) return configuredMaps;

  const knownMaps = mapDocuments
    .map((map) => normalizeMapName(map?.name || map?.mapName || map?.id))
    .filter(Boolean);
  return knownMaps.length ? knownMaps : DEFAULT_ACTIVE_MAPS;
}

export function resolveMapDocument(mapDocuments = [], mapName = "") {
  const normalizedName = normalizeMapName(mapName).toLowerCase();
  return mapDocuments.find((map) => {
    const candidate = normalizeMapName(map?.name || map?.mapName || map?.id).toLowerCase();
    return candidate === normalizedName;
  }) || null;
}

export function createUnplannedMatchEvent({
  eventId,
  matchId,
  mapId = null,
  mapName = null,
  playedAt,
  user,
}) {
  const timestamp = playedAt || new Date().toISOString();
  const username = cleanText(user?.username || user?.displayName || user?.email || "Member", 120);
  const uid = cleanText(user?.uid, 160);

  return {
    eventId,
    matchId,
    sessionPlanId: null,
    startUTC: timestamp,
    endUTC: timestamp,
    repeat: "none",
    recurrenceId: null,
    exceptions: [],
    overrides: {},
    repeatEndDate: null,
    tag: UNPLANNED_MATCH_TAG,
    calendar: "team",
    reminder: "",
    note: "Created automatically from a recording or VOD upload.",
    mapId: cleanText(mapId, 160) || null,
    mapName: normalizeMapName(mapName) || null,
    participants: username ? [username] : [],
    participantsUid: uid ? [uid] : [],
    participantStates: {},
    createdAt: timestamp,
    createdBy: username || "Member",
    createdByUid: uid || null,
    lastEditedBy: username || "Member",
    lastEditedByUid: uid || null,
    lastEditedAt: Date.now(),
    isUnplannedMatch: true,
  };
}

export function createMentionTokens(text, members = [], groups = {}) {
  const value = String(text || "");
  const membersByName = new Map(
    members
      .filter((member) => member?.uid && member?.username)
      .map((member) => [String(member.username).toLowerCase(), member]),
  );
  const seen = new Set();
  const mentions = [];
  const addMemberMention = (member) => {
    if (!member?.uid || !member?.username || seen.has(member.uid)) return;
    seen.add(member.uid);
    mentions.push({ type: "user", uid: member.uid, username: member.username });
  };
  const normalizedRole = (member) => String(member?.roleLower || member?.role || "")
    .trim()
    .toLowerCase();
  const coachingStaff = Array.isArray(groups.coachingStaff)
    ? groups.coachingStaff
    : members.filter((member) => ["analyst", "coach"].includes(normalizedRole(member)));
  const subs = Array.isArray(groups.subs)
    ? groups.subs
    : members.filter((member) => ["sub", "subs", "substitute", "substitutes"].includes(normalizedRole(member)));
  const managers = Array.isArray(groups.managers)
    ? groups.managers
    : members.filter((member) => ["manager", "managers", "manger"].includes(normalizedRole(member)));

  if (/@\s*coaching\s+staff\b/i.test(value)) coachingStaff.forEach(addMemberMention);
  if (/@\s*subs\b/i.test(value)) subs.forEach(addMemberMention);
  if (/@\s*managers\b/i.test(value)) managers.forEach(addMemberMention);

  const mentionPattern = /@([a-zA-Z0-9_.-]+)/g;
  let match;

  while ((match = mentionPattern.exec(value))) {
    const username = String(match[1] || "").trim();
    if (!username) continue;

    if (username.toLowerCase() === "team") {
      if (!seen.has("team")) {
        seen.add("team");
        mentions.push({ type: "team", label: "Team" });
      }
      continue;
    }

    addMemberMention(membersByName.get(username.toLowerCase()));
  }

  return mentions;
}

export function hasPlanningAuthority(team = {}, uid) {
  if (!uid) return false;
  const authorityUids = [
    team.ownerUid,
    team.leaderUid,
    team.createdByUid,
    ...(Array.isArray(team.adminUids) ? team.adminUids : []),
    ...(Array.isArray(team.managerUids) ? team.managerUids : []),
  ].filter(Boolean);

  // Existing teams may not have a modern authority field yet. Do not make their
  // planner unusable until those fields are populated by the team-management flow.
  return authorityUids.length === 0 || authorityUids.includes(uid);
}
